import { useEffect, useState } from 'react';

export interface UseInViewOptions {
  /** Margin around the root used to pre-load tiles just before they scroll in. */
  rootMargin?: string;
  /** Once true, stay true — widgets keep their data once mounted. */
  once?: boolean;
}

/**
 * Returns whether the referenced element is (or has been) within the viewport.
 *
 * Used by `WidgetTile` to DEFER mounting a live widget — and therefore its
 * on-mount data fetch — until the tile is actually visible. Falls back to
 * `true` when `IntersectionObserver` is unavailable (SSR / old jsdom) so the
 * canvas still renders everything.
 */
export function useInView(
  ref: React.RefObject<Element | null>,
  { rootMargin = '200px', once = true }: UseInViewOptions = {},
): boolean {
  const [inView, setInView] = useState<boolean>(
    () => typeof IntersectionObserver === 'undefined',
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, rootMargin, once]);

  return inView;
}
